import React, { useState, useMemo } from "react";
import ReactJson from "@microlink/react-json-view";
import "./System_logs.css";

export function System_logs({ groups = {} }) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [localLevelFilter, setLocalLevelFilter] = useState("all");
  const [expandedRows, setExpandedRows] = useState({});
  const [openingVSCode, setOpeningVSCode] = useState(null);

  // ============================================================
  // HELPERS
  // ============================================================

  const getLogsArray = (group) => {
    if (!group?.logs) return [];

    return Array.isArray(group.logs) ? group.logs : [group.logs];
  };

  const getStatusClass = (group) => {
    const logs = getLogsArray(group);

    if (logs.some((log) => log?.level?.toLowerCase() === "error")) {
      return "error";
    }

    if (logs.some((log) => log?.level?.toLowerCase() === "warning")) {
      return "warning";
    }

    return "normal";
  };

  const formatTime = (logTime) => {
    if (!logTime) return "—";

    try {
      const date = new Date(logTime);

      return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return logTime;
    }
  };

  // ============================================================
  // GLOBAL FILTER
  // ============================================================

  const processedGroups = useMemo(() => {
    return Object.entries(groups)
      .map(([name, group]) => {
        let logs = getLogsArray(group);

        // IMPORTANT:
        // If a level is selected, only logs of that level remain.
        if (levelFilter !== "all") {
          logs = logs.filter(
            (log) => log?.level?.toLowerCase() === levelFilter,
          );
        }

        // Search group name
        if (
          searchTerm &&
          !name.toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          return null;
        }

        // Don't show empty groups after filtering
        if (logs.length === 0) {
          return null;
        }

        return {
          name,
          originalCount: group.count,
          logs,
          statusClass: getStatusClass({
            logs: logs,
          }),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const priority = {
          error: 3,
          warning: 2,
          normal: 1,
        };

        return priority[b.statusClass] - priority[a.statusClass];
      });
  }, [groups, searchTerm, levelFilter]);

  // ============================================================
  // CURRENT GROUP
  // ============================================================

  const current = processedGroups.find((group) => group.name === selectedGroup);

  const allLogsInGroup = current?.logs || [];

  // ============================================================
  // LOCAL LEVEL FILTER
  // ============================================================

  const currentLogs = useMemo(() => {
    if (localLevelFilter === "all") {
      return allLogsInGroup;
    }

    return allLogsInGroup.filter(
      (log) => log?.level?.toLowerCase() === localLevelFilter,
    );
  }, [allLogsInGroup, localLevelFilter]);

  // ============================================================
  // LEVEL COUNTS
  // ============================================================

  const levelCounts = useMemo(() => {
    const counts = {
      error: 0,
      warning: 0,
      info: 0,
      critical: 0,
      all: 0,
    };

    allLogsInGroup.forEach((log) => {
      const level = log?.level?.toLowerCase() || "info";

      if (counts[level] !== undefined) {
        counts[level]++;
      }
    });

    counts.all = allLogsInGroup.length;

    return counts;
  }, [allLogsInGroup]);

  // ============================================================
  // EXPAND / COLLAPSE
  // ============================================================

  const toggleExpand = (logId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  // ============================================================
  // SELECT GROUP
  // ============================================================

  const handleSelectGroup = (name) => {
    setSelectedGroup(name);
    setLocalLevelFilter("all");
    setExpandedRows({});
    setOpeningVSCode(null);
  };

  // ============================================================
  // OPEN VS CODE
  // ============================================================

  const handleOpenVSCode = (log, logId) => {
    setOpeningVSCode(logId);

    try {
      if (!log?.file) {
        console.error("No file information available.");
        setOpeningVSCode(null);
        return;
      }

      const line = log?.line || 1;

      const vscodeUrl = `vscode://file/${log.file}:${line}`;

      // Trigger VS Code
      window.location.href = vscodeUrl;

      // Keep loading state visible while VS Code starts
      setTimeout(() => {
        setOpeningVSCode(null);
      }, 3000);
    } catch (error) {
      console.error("Failed to open VS Code:", error);

      setOpeningVSCode(null);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="main-card">
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="card-header">
        <div className="header-left">
          <h2>System Logs</h2>

          <span className="total-count">
            {Object.keys(groups).length} groups
          </span>
        </div>

        <div className="filters">
          <input
            type="text"
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="level-select"
          >
            <option value="all">All levels</option>

            <option value="error">Error only</option>

            <option value="warning">Warning only</option>

            <option value="info">Info only</option>

            <option value="critical">Critical only</option>
          </select>
        </div>
      </div>

      {/* ======================================================
          TWO COLUMN LAYOUT
      ====================================================== */}

      <div className="grid-container">
        {/* ====================================================
            LEFT PANEL
        ==================================================== */}

        <div className="left-panel">
          {processedGroups.length === 0 ? (
            <div className="empty-state">No matching groups</div>
          ) : (
            processedGroups.map((group) => (
              <div
                key={group.name}
                className={`
                  group-item
                  ${group.statusClass}
                  ${selectedGroup === group.name ? "active" : ""}
                `}
                onClick={() => handleSelectGroup(group.name)}
              >
                <span className="group-name">{group.name}</span>

                <span className="group-count">{group.logs.length}</span>
              </div>
            ))
          )}
        </div>

        {/* ====================================================
            RIGHT PANEL
        ==================================================== */}

        <div className="right-panel">
          {!selectedGroup || !current ? (
            <div className="empty-state">
              Select a group from the left to view details
            </div>
          ) : (
            <>
              {/* ==================================================
                  DETAILS HEADER
              ================================================== */}

              <div className="details-header">
                <div className="details-title">
                  <h3>{selectedGroup}</h3>

                  <span
                    className={`
                      status-badge
                      ${current.statusClass}
                    `}
                  >
                    {current.statusClass}
                  </span>
                </div>

                {/* LEVEL COUNTS */}

                <div className="level-counts">
                  {/* ALL */}

                  <button
                    className={`
                      count-badge all
                      ${localLevelFilter === "all" ? "active" : ""}
                    `}
                    onClick={() => setLocalLevelFilter("all")}
                  >
                    All {levelCounts.all}
                  </button>

                  {/* ERROR */}

                  {levelCounts.error > 0 && (
                    <button
                      className={`
                        count-badge error
                        ${localLevelFilter === "error" ? "active" : ""}
                      `}
                      onClick={() => setLocalLevelFilter("error")}
                    >
                      Error {levelCounts.error}
                    </button>
                  )}

                  {/* WARNING */}

                  {levelCounts.warning > 0 && (
                    <button
                      className={`
                        count-badge warning
                        ${localLevelFilter === "warning" ? "active" : ""}
                      `}
                      onClick={() => setLocalLevelFilter("warning")}
                    >
                      Warning {levelCounts.warning}
                    </button>
                  )}

                  {/* INFO */}

                  {levelCounts.info > 0 && (
                    <button
                      className={`
                        count-badge info
                        ${localLevelFilter === "info" ? "active" : ""}
                      `}
                      onClick={() => setLocalLevelFilter("info")}
                    >
                      Info {levelCounts.info}
                    </button>
                  )}

                  {/* CRITICAL */}

                  {levelCounts.critical > 0 && (
                    <button
                      className={`
                        count-badge critical
                        ${localLevelFilter === "critical" ? "active" : ""}
                      `}
                      onClick={() => setLocalLevelFilter("critical")}
                    >
                      Critical {levelCounts.critical}
                    </button>
                  )}
                </div>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="table-wrapper">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}></th>

                      <th style={{ width: "120px" }}>Time</th>

                      <th style={{ width: "100px" }}>Level</th>

                      <th>Message</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentLogs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="empty-table">
                          No logs for this level
                        </td>
                      </tr>
                    ) : (
                      currentLogs.map((log, i) => {
                        const level = log?.level?.toLowerCase() || "info";

                        /*
                         * Stable ID for this log.
                         *
                         * We don't use only `i` because
                         * filtering changes the index.
                         */

                        const logId = [
                          log?.log_time || "",
                          log?.instance_id || "",
                          log?.file || "",
                          log?.line || "",
                          i,
                        ].join("-");

                        const isExpanded = expandedRows[logId];

                        /*
                         * A row can be expanded when
                         * there is useful information.
                         */

                        const hasDetails = Boolean(
                          log?.log_time ||
                          log?.instance_id ||
                          log?.message ||
                          log?.file ||
                          log?.line ||
                          log?.function ||
                          log?.error_type ||
                          log?.code ||
                          log?.debug,
                        );

                        return (
                          <React.Fragment key={logId}>
                            {/* ==================================
                                MAIN ROW
                            ================================== */}

                            <tr
                              className={`
                                log-row
                                ${level}
                              `}
                            >
                              {/* ARROW */}

                              <td>
                                {hasDetails && (
                                  <button
                                    className="expand-btn"
                                    onClick={() => toggleExpand(logId)}
                                    aria-label={
                                      isExpanded
                                        ? "Collapse details"
                                        : "Expand details"
                                    }
                                  >
                                    {isExpanded ? "▼" : "▶"}
                                  </button>
                                )}
                              </td>

                              {/* TIME */}

                              <td className="time-cell">
                                {formatTime(log?.log_time)}
                              </td>

                              {/* LEVEL */}

                              <td>
                                <span
                                  className={`
                                    level-tag
                                    ${level}
                                  `}
                                >
                                  {level}
                                </span>
                              </td>

                              {/* MESSAGE */}

                              <td className="message-cell">
                                {log?.message || "—"}
                              </td>
                            </tr>

                            {/* ==================================
                                EXPANDED DETAILS
                            ================================== */}

                            {isExpanded && hasDetails && (
                              <tr className="details-row">
                                <td colSpan="6">
                                  <div className="expanded-details">
                                    {/* =================================
                                        BASIC INFORMATION
                                    ================================= */}

                                    <div className="detail-grid">
                                      {/* FULL TIME */}

                                      <div className="detail-item">
                                        <span>Full Time</span>

                                        <strong>{log?.log_time || "—"}</strong>
                                      </div>

                                      {/* INSTANCE ID */}

                                      <div className="detail-item">
                                        <span>Instance ID</span>

                                        <strong>
                                          {log?.instance_id || "—"}
                                        </strong>
                                      </div>

                                      {/* FULL MESSAGE */}

                                      <div className="detail-item full-width">
                                        <span>Full Message</span>

                                        <strong className="full-message">
                                          {log?.message || "—"}
                                        </strong>
                                      </div>

                                      {/* VS CODE */}

                                      {log?.file && (
                                        <div className="detail-item">
                                          <span>VS Code</span>

                                          <button
                                            type="button"
                                            className={`
                                              vscode_link
                                              ${
                                                openingVSCode === logId
                                                  ? "opening"
                                                  : ""
                                              }
                                            `}
                                            onClick={() =>
                                              handleOpenVSCode(log, logId)
                                            }
                                            disabled={openingVSCode === logId}
                                          >
                                            {openingVSCode === logId ? (
                                              <>
                                                <span className="vscode_spinner"></span>
                                                Opening VS Code...
                                              </>
                                            ) : (
                                              <>
                                                <span className="vscode_icon">
                                                  VS
                                                </span>
                                                Open in VS Code
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* =================================
                                        ERROR INFORMATION
                                    ================================= */}

                                    {level === "error" && (
                                      <>
                                        <div className="error-details-divider">
                                          ERROR DETAILS
                                        </div>

                                        <div className="detail-grid">
                                          {/* FILE */}

                                          <div className="detail-item">
                                            <span>File</span>

                                            <strong>{log?.file || "—"}</strong>
                                          </div>

                                          {/* ERROR TYPE */}

                                          <div className="detail-item">
                                            <span>Error Type</span>

                                            <strong>
                                              {log?.error_type || "—"}
                                            </strong>
                                          </div>

                                          {/* FUNCTION */}

                                          <div className="detail-item">
                                            <span>Function</span>

                                            <strong>
                                              {log?.function || "—"}
                                            </strong>
                                          </div>

                                          {/* LINE */}

                                          <div className="detail-item">
                                            <span>Line</span>

                                            <strong>{log?.line || "—"}</strong>
                                          </div>

                                          {/* CODE */}

                                          <div className="detail-item full-width">
                                            <span>Code</span>

                                            <strong className="code-value">
                                              {log?.code || "—"}
                                            </strong>
                                          </div>
                                        </div>

                                        {/* DEBUG */}

                                        {log?.debug && (
                                          <div className="debug-section">
                                            <span>Snap Details</span>

                                            <ReactJson
                                              src={log.debug}
                                              collapsed={2}
                                              enableClipboard={true}
                                              displayDataTypes={false}
                                              name={false}
                                              theme="monokai"
                                              style={{
                                                background: "transparent",
                                                fontSize: "12px",
                                              }}
                                            />
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
